import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Eye, Save, Upload, FileText, Download, Pencil, Folder, Users } from "lucide-react";
import { toast } from "sonner";

export const TEMPLATE_PLACEHOLDERS = ["employee_name", "preferred_name", "employee_code", "email", "candidate_name", "job_title", "salary", "start_date", "hours_per_week", "holiday_weeks", "probation_months", "notice_period", "department", "employment_type", "org_name", "org_address", "today", "signature_name", "signed_date"];

const toHtml = (body) => {
  if (!body) return "";
  if (body.includes("<")) return body;
  return body.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
};

export function RichTextEditor({ value, onChange, testid, fields }) {
  const ref = useRef(null);
  const [showFields, setShowFields] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== toHtml(value)) el.innerHTML = toHtml(value);
  }, [value]);
  const cmd = (c, val) => { document.execCommand(c, false, val); if (ref.current) { ref.current.focus(); onChange(ref.current.innerHTML); } };
  const insertField = (f) => { if (ref.current) ref.current.focus(); document.execCommand("insertText", false, `{{${f}}}`); if (ref.current) onChange(ref.current.innerHTML); setShowFields(false); };
  const Btn = ({ c, val, children }) => (
    <button type="button" onMouseDown={(e) => { e.preventDefault(); cmd(c, val); }} className="px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-100" data-testid={`rte-${c}`}>{children}</button>
  );
  return (
    <div>
      <div className="flex gap-1 mb-1 flex-wrap items-center">
        <Btn c="bold"><b>B</b></Btn><Btn c="italic"><i>I</i></Btn><Btn c="underline"><u>U</u></Btn>
        <Btn c="formatBlock" val="<h2>">Heading</Btn><Btn c="formatBlock" val="<p>">Body</Btn>
        <Btn c="insertUnorderedList">• List</Btn>
        {fields?.length > 0 && (
          <div className="relative">
            <button type="button" onMouseDown={(e) => { e.preventDefault(); setShowFields((s) => !s); }} className="px-2 py-1 text-xs border border-[#2EA3FD] text-[#2EA3FD] rounded hover:bg-[#EFF8FF] font-medium" data-testid="rte-insert-field">+ Merge field</button>
            {showFields && (
              <div className="absolute z-50 mt-1 w-48 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1" data-testid="rte-field-menu">
                {fields.map((f) => (
                  <button key={f} type="button" onMouseDown={(e) => { e.preventDefault(); insertField(f); }} className="block w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 font-mono" data-testid={`rte-field-${f}`}>{`{{${f}}}`}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="min-h-[240px] border border-slate-200 rounded-lg p-3 text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#2EA3FD]" data-testid={testid} />
    </div>
  );
}

export default function TemplatesManager() {
  const [sections, setSections] = useState([]);
  const [tpls, setTpls] = useState([]);
  const [files, setFiles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newSec, setNewSec] = useState({ name: "", parent_id: "none" });
  const [upload, setUpload] = useState(null); // { section_id, title, file }
  const [bulk, setBulk] = useState(null); // { template_key, scope_type, department, manager_id }
  const [emps, setEmps] = useState([]);
  const [previewEmp, setPreviewEmp] = useState("");
  const previewForPerson = async () => {
    if (!previewEmp) { toast.error("Choose an employee"); return; }
    try { const r = await api.post(`/employees/${previewEmp}/preview-letter`, { template_key: editing.key, body: editing.body, title: editing.title }, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openBulk = (key) => { setBulk({ template_key: key, scope_type: "all", department: "", manager_id: "" }); if (!emps.length) api.get("/employees").then((r) => setEmps(r.data)).catch(() => {}); };
  const runBulk = async () => {
    try { const { data } = await api.post("/letters/bulk", { template_key: bulk.template_key, scope_type: bulk.scope_type, department: bulk.department || null, manager_id: bulk.manager_id || null }); toast.success(data.message); setBulk(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const depts = [...new Set(emps.map((e) => e.department).filter(Boolean))];

  const loadAll = () => {
    api.get("/sections").then((r) => setSections(r.data)).catch(() => {});
    api.get("/templates").then((r) => setTpls(r.data)).catch(() => {});
    api.get("/template-files").then((r) => setFiles(r.data)).catch(() => {});
    api.get("/employees").then((r) => setEmps(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const tops = sections.filter((s) => !s.parent_id).sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const subsOf = (pid) => sections.filter((s) => s.parent_id === pid);
  const hasUncategorised = tpls.some((t) => !t.section_id) || files.some((f) => !f.section_id);

  const [dragSec, setDragSec] = useState(null);
  const [dragTpl, setDragTpl] = useState(null);
  const reorderSecs = async (overId) => {
    if (!dragSec || dragSec === overId) { setDragSec(null); return; }
    const ids = tops.map((s) => s.id);
    const from = ids.indexOf(dragSec), to = ids.indexOf(overId);
    setDragSec(null);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setSections((prev) => prev.map((s) => (s.parent_id ? s : { ...s, order: ids.indexOf(s.id) })));
    try { await api.post("/sections/reorder", { ids }); } catch { toast.error("Could not reorder"); }
  };
  const reorderTpls = async (sid, overKey) => {
    if (!dragTpl || dragTpl.key === overKey || dragTpl.sid !== (sid || null)) { setDragTpl(null); return; }
    const keys = tpls.filter((x) => (x.section_id || null) === (sid || null)).sort((a, b) => (a.order ?? 999) - (b.order ?? 999)).map((x) => x.key);
    const from = keys.indexOf(dragTpl.key), to = keys.indexOf(overKey);
    setDragTpl(null);
    if (from < 0 || to < 0) return;
    keys.splice(to, 0, keys.splice(from, 1)[0]);
    setTpls((prev) => prev.map((x) => (keys.includes(x.key) ? { ...x, order: keys.indexOf(x.key) } : x)));
    try { await api.post("/templates/reorder", { ids: keys }); } catch { toast.error("Could not reorder"); }
  };

  const addSection = async () => {
    if (!newSec.name.trim()) { toast.error("Enter a section name"); return; }
    try { await api.post("/sections", { name: newSec.name, parent_id: newSec.parent_id === "none" ? null : newSec.parent_id }); setNewSec({ name: "", parent_id: "none" }); loadAll(); toast.success("Section added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const renameSection = async (s) => { const name = window.prompt("Rename section", s.name); if (name && name.trim()) { await api.put(`/sections/${s.id}`, { name }); loadAll(); } };
  const delSection = async (s) => { if (window.confirm(`Delete section "${s.name}"? Its items move to Uncategorised.`)) { await api.delete(`/sections/${s.id}`); loadAll(); } };

  const createTpl = async (sid) => {
    const name = window.prompt("New template name (e.g. Disciplinary warning letter)"); if (!name || !name.trim()) return;
    try { const { data } = await api.post("/templates", { name, title: name, body: "", section_id: sid === "none" ? null : sid }); loadAll(); setEditing(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveTpl = async () => {
    try { await api.put(`/templates/${editing.key}`, { name: editing.name, title: editing.title, body: editing.body, section_id: editing.section_id || null }); toast.success("Template saved"); setEditing(null); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const previewTpl = async (t) => { try { const r = await api.post(`/templates/${t.key}/preview`, { title: t.title, body: t.body }, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delTpl = async (t) => { if (["offer_letter", "contract"].includes(t.key)) { toast.error("Built-in templates can't be deleted"); return; } if (window.confirm(`Delete template "${t.name}"?`)) { await api.delete(`/templates/${t.key}`); loadAll(); } };
  const importInto = async (file) => { if (!file || !editing) return; const text = await file.text(); const html = text.includes("<") ? text : text.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join(""); setEditing({ ...editing, body: html }); toast.success("Imported — review & Save"); };

  const doUpload = async () => {
    if (!upload.title.trim() || !upload.file) { toast.error("Add a title and choose a file"); return; }
    const fd = new FormData(); fd.append("title", upload.title); fd.append("section_id", upload.section_id === "none" ? "" : upload.section_id); fd.append("file", upload.file);
    try { await api.post("/template-files", fd); toast.success("File uploaded"); setUpload(null); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const dlFile = async (f) => { try { const r = await api.get(`/template-files/${f.id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch { toast.error("Not available"); } };
  const delFile = async (f) => { if (window.confirm(`Delete "${f.title}"?`)) { await api.delete(`/template-files/${f.id}`); loadAll(); } };

  const Items = ({ sid }) => {
    const t = tpls.filter((x) => (x.section_id || null) === (sid || null)).sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    const f = files.filter((x) => (x.section_id || null) === (sid || null));
    return (
      <div className="space-y-2">
        {t.map((tp) => (
          <div key={tp.key} draggable onDragStart={() => setDragTpl({ key: tp.key, sid: sid || null })} onDragOver={(e) => e.preventDefault()} onDrop={() => reorderTpls(sid, tp.key)}
            className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2 cursor-move" data-testid={`template-item-${tp.key}`}>
            <span className="text-sm font-medium text-[#0B1A2F] flex items-center gap-2 min-w-0"><FileText size={14} className="text-[#2EA3FD] shrink-0" /><span className="truncate">{tp.name}</span>{["offer_letter", "contract"].includes(tp.key) && <span className="text-[10px] text-slate-400">built-in</span>}</span>
            <div className="flex gap-0.5 shrink-0">
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditing(tp)} data-testid={`template-edit-${tp.key}`}><Pencil size={13} /></Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => previewTpl(tp)}><Eye size={13} /></Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[#0B1A2F]" onClick={() => openBulk(tp.key)} data-testid={`template-bulk-${tp.key}`} title="Bulk generate for a team/department"><Users size={13} /></Button>
              {!["offer_letter", "contract"].includes(tp.key) && <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => delTpl(tp)} data-testid={`template-del-${tp.key}`}><Trash2 size={13} /></Button>}
            </div>
          </div>
        ))}
        {f.map((fl) => (
          <div key={fl.id} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2" data-testid={`template-file-${fl.id}`}>
            <button onClick={() => dlFile(fl)} className="text-sm font-medium text-[#0B1A2F] flex items-center gap-2 hover:text-[#2EA3FD] min-w-0"><Download size={14} className="shrink-0" /><span className="truncate">{fl.title}</span><span className="text-xs text-slate-400 shrink-0">({fl.filename})</span></button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600 shrink-0" onClick={() => delFile(fl)} data-testid={`template-file-del-${fl.id}`}><Trash2 size={13} /></Button>
          </div>
        ))}
        {t.length === 0 && f.length === 0 && <p className="text-xs text-slate-400">No items yet.</p>}
        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => createTpl(sid || "none")} data-testid={`add-template-${sid || "none"}`}><Plus size={13} className="mr-1" />New template</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setUpload({ section_id: sid || "none", title: "", file: null })} data-testid={`add-file-${sid || "none"}`}><Upload size={13} className="mr-1" />Upload file</Button>
        </div>
      </div>
    );
  };

  const Tile = ({ section }) => (
    <div draggable onDragStart={() => setDragSec(section.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => reorderSecs(section.id)}
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`section-tile-${section.id}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><Folder size={16} className="text-[#2EA3FD]" />{section.name}</h3>
        {!section.system && (
          <div className="flex gap-0.5">
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => renameSection(section)} data-testid={`section-rename-${section.id}`}><Pencil size={13} /></Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => delSection(section)} data-testid={`section-del-${section.id}`}><Trash2 size={13} /></Button>
          </div>
        )}
      </div>
      <Items sid={section.id} />
      {subsOf(section.id).map((sub) => (
        <div key={sub.id} className="mt-3 ml-3 pl-3 border-l-2 border-slate-100" data-testid={`subsection-${sub.id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-600">{sub.name}</p>
            <div className="flex gap-0.5">
              <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => renameSection(sub)}><Pencil size={12} /></Button>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-rose-600" onClick={() => delSection(sub)}><Trash2 size={12} /></Button>
            </div>
          </div>
          <Items sid={sub.id} />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="templates-manager">
      <p className="text-sm text-slate-500">Organise letter templates and reference files into sections. Rich-text templates use placeholders like <code className="text-[#2EA3FD]">{"{{candidate_name}}"}</code> and generate a branded PDF; a signature certificate is appended when signed. Sections are shared with Documents &amp; Policies.</p>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
        <p className="text-xs font-semibold text-slate-500">Available placeholders</p>
        <div className="flex flex-wrap gap-1.5">
          {TEMPLATE_PLACEHOLDERS.map((p) => <code key={p} className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{`{{${p}}}`}</code>)}
        </div>
        <div className="pt-2 border-t border-slate-100 flex items-end gap-2 flex-wrap">
          <div><Label className="text-xs text-slate-500">New section</Label><Input value={newSec.name} onChange={(e) => setNewSec({ ...newSec, name: e.target.value })} placeholder="e.g. Health & Safety" className="w-52" data-testid="new-section-name" /></div>
          <div><Label className="text-xs text-slate-500">Parent (optional)</Label>
            <Select value={newSec.parent_id} onValueChange={(v) => setNewSec({ ...newSec, parent_id: v })}>
              <SelectTrigger className="w-48" data-testid="new-section-parent"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Top-level</SelectItem>{tops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button className="bg-[#0B1A2F] hover:bg-[#162B48]" onClick={addSection} data-testid="add-section-btn"><Plus size={15} className="mr-1" />Add section</Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {tops.map((s) => <Tile key={s.id} section={s} />)}
        {hasUncategorised && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="section-tile-uncategorised">
            <h3 className="font-semibold text-[#0B1A2F] flex items-center gap-2 mb-3"><Folder size={16} className="text-slate-400" />Uncategorised</h3>
            <Items sid={null} />
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-edit-dialog">
          <DialogHeader><DialogTitle>Edit template</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Template name</Label><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="template-edit-name" /></div>
                <div><Label className="text-xs text-slate-500">Section</Label>
                  <Select value={editing.section_id || "none"} onValueChange={(v) => setEditing({ ...editing, section_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="template-edit-section"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Uncategorised</SelectItem>{sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.parent_id ? "— " : ""}{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs text-slate-500">Document title</Label><Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="template-edit-title" /></div>
              <div><Label className="text-xs text-slate-500">Body</Label><RichTextEditor value={editing.body || ""} onChange={(html) => setEditing({ ...editing, body: html })} testid="template-edit-body" fields={TEMPLATE_PLACEHOLDERS} /></div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={saveTpl} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="template-edit-save"><Save size={15} className="mr-2" />Save</Button>
                <Button variant="outline" onClick={() => previewTpl(editing)}><Eye size={15} className="mr-2" />Preview PDF</Button>
                <label className="inline-flex items-center gap-1.5 text-sm border border-slate-200 rounded-md px-3 py-1.5 cursor-pointer hover:bg-slate-50" data-testid="template-edit-import"><Upload size={15} />Import<input type="file" accept=".html,.htm,.txt,.md" className="hidden" onChange={(e) => importInto(e.target.files?.[0])} /></label>
              </div>
              <div className="flex items-end gap-2 flex-wrap bg-[#EFF8FF] rounded-lg p-3">
                <div className="flex-1 min-w-[12rem]">
                  <Label className="text-xs text-slate-500">Preview filled for a specific person</Label>
                  <Select value={previewEmp} onValueChange={setPreviewEmp}>
                    <SelectTrigger data-testid="template-preview-person-select"><SelectValue placeholder="Choose employee…" /></SelectTrigger>
                    <SelectContent className="max-h-72">{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}{e.department ? ` · ${e.department}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={previewForPerson} data-testid="template-preview-person-btn"><Eye size={15} className="mr-2" />Preview for person</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!upload} onOpenChange={(o) => !o && setUpload(null)}>
        <DialogContent data-testid="template-file-dialog">
          <DialogHeader><DialogTitle>Upload a reference file</DialogTitle></DialogHeader>
          {upload && (
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">Title</Label><Input value={upload.title} onChange={(e) => setUpload({ ...upload, title: e.target.value })} placeholder="e.g. Staff handbook" data-testid="template-file-title" /></div>
              <div><Label className="text-xs text-slate-500">Section</Label>
                <Select value={upload.section_id} onValueChange={(v) => setUpload({ ...upload, section_id: v })}>
                  <SelectTrigger data-testid="template-file-section"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Uncategorised</SelectItem>{sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.parent_id ? "— " : ""}{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <input type="file" onChange={(e) => setUpload({ ...upload, file: e.target.files?.[0] || null })} className="text-xs" data-testid="template-file-input" />
              <DialogFooter><Button variant="outline" onClick={() => setUpload(null)}>Cancel</Button><Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={doUpload} data-testid="template-file-upload-btn">Upload</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
        <DialogContent data-testid="bulk-letter-dialog">
          <DialogHeader><DialogTitle>Bulk generate letters</DialogTitle></DialogHeader>
          {bulk && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">The same letter will be generated and filed onto each matching employee's record, with their own details merged in.</p>
              <div><Label className="text-xs text-slate-500">Who for</Label>
                <Select value={bulk.scope_type} onValueChange={(v) => setBulk({ ...bulk, scope_type: v })}>
                  <SelectTrigger data-testid="bulk-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Everyone (all active employees)</SelectItem><SelectItem value="department">A department</SelectItem><SelectItem value="manager">A manager's team</SelectItem></SelectContent>
                </Select>
              </div>
              {bulk.scope_type === "department" && (
                <div><Label className="text-xs text-slate-500">Department</Label>
                  <Select value={bulk.department} onValueChange={(v) => setBulk({ ...bulk, department: v })}>
                    <SelectTrigger data-testid="bulk-dept-select"><SelectValue placeholder="Choose department…" /></SelectTrigger>
                    <SelectContent>{depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {bulk.scope_type === "manager" && (
                <div><Label className="text-xs text-slate-500">Manager (letters go to their direct reports)</Label>
                  <Select value={bulk.manager_id} onValueChange={(v) => setBulk({ ...bulk, manager_id: v })}>
                    <SelectTrigger data-testid="bulk-manager-select"><SelectValue placeholder="Choose manager…" /></SelectTrigger>
                    <SelectContent className="max-h-72">{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter><Button variant="outline" onClick={() => setBulk(null)}>Cancel</Button><Button className="bg-[#0B1A2F] hover:bg-[#162B48]" onClick={runBulk} data-testid="bulk-generate-btn"><Users size={15} className="mr-2" />Generate letters</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
